/**
 * Custom Layout — "Pattern A" (hybrid-3c §3), taken from the reference
 * implementation (roommatik.console.ui).
 *
 * A full-height rail with the app bar absolute over the content column only.
 * Desktop locks the viewport height and the content column owns the one
 * vertical scroll; a phone gets the rail as a drawer and a fixed app bar. The
 * top inset grows by the section bar's height on the screens that show it.
 */
import React from 'react';
import { Layout, LayoutProps } from 'react-admin';
import { useTheme, useMediaQuery } from '@mui/material';
import { CustomAppBar } from './CustomAppBar';
import { CustomMenu } from './CustomMenu';
import { APP_BAR_HEIGHT, useSectionTabs } from './useSectionTabs';
import { FULL_VIEWPORT_HEIGHT } from '@/lightTheme';
import { Navigate, useLocation } from 'react-router-dom';
import { usePanelAccess } from '@/features/developers/access';
import { NoAccessNotice } from '@/features/developers/AccessNotice';

export const CustomLayout: React.FC<LayoutProps> = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // The section bar hangs below the app bar and is absolutely positioned too, so the content
  // column's top inset has to grow by its height on the screens that show it.
  const { height: sectionTabsHeight } = useSectionTabs();
  const location = useLocation();
  const access = usePanelAccess();

  // Every route passes through here, so a page added later is covered without anyone
  // remembering to: a documentation-only user is sent to Developers from anywhere, and a user
  // with no access gets the notice that says who to ask.
  const content =
    access === 'none' ? (
      <NoAccessNotice />
    ) : access === 'documentation' && location.pathname !== '/developers' ? (
      <Navigate to="/developers" replace />
    ) : (
      props.children
    );

  return (
    <Layout
      {...props}
      appBar={CustomAppBar}
      menu={CustomMenu}
      sx={{
        '&': {
          backgroundColor: theme.palette.background.default,
          // react-admin's Layout root ships `min-width: fit-content`, which lets any
          // wide intrinsic content (e.g. a long MuiTabs row inside the config forms)
          // stretch the whole shell past the viewport and produce a page-level
          // horizontal scrollbar. Wide content must scroll inside its own container,
          // never the page body.
          minWidth: 0,
        },

        ...(!isMobile && {
          ...FULL_VIEWPORT_HEIGHT,
          overflow: 'hidden',
        }),

        '& .RaLayout-appFrame': {
          marginTop: 0,
          alignItems: 'flex-start',
          // Anchor for the desktop AppBar, which is absolutely positioned over
          // the content column only (see CustomAppBar).
          ...(!isMobile && { height: '100%', position: 'relative' }),
        },

        // The rail's Drawer chain (.RaSidebar-docked > -paper > -fixed) is styled
        // in the theme's RaSidebar override: declared there it is merged into
        // react-admin's own rule for the slot and wins on order, with no need to
        // force anything from here.

        '& .RaLayout-contentWithSidebar': {
          display: 'flex',
          flex: 1,
          gap: 0,
          width: '100%',
          // Pattern A: no outer padding — the rail is flush to the left/top/
          // bottom edges and the row spans the full viewport height (the AppBar
          // is absolute, so this row starts at y=0).
          ...(!isMobile && {
            padding: 0,
            ...FULL_VIEWPORT_HEIGHT,
            overflow: 'hidden',
          }),
        },

        '& .RaLayout-content': {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,

          ...(isMobile && {
            // Exactly one viewport tall. The top strip (the absolute AppBar plus the section tab
            // strip) is reserved by the padding below, and under border-box sizing that padding
            // already comes out of this height — subtracting it from the height as well took the
            // strip out twice and left a band of dead canvas at the foot of every page, with a
            // sticky action bar floating above the bottom edge.
            ...FULL_VIEWPORT_HEIGHT,
            // From the constant, not a literal. It read 48 while the bar is 52, so the
            // top four pixels of every page sat under the app bar on a phone.
            paddingTop: `${APP_BAR_HEIGHT + sectionTabsHeight}px`,
            paddingBottom: 0,
            overflow: 'hidden',
          }),

          ...(!isMobile && {
            ...FULL_VIEWPORT_HEIGHT,
            // Reserve the top strip for the absolute AppBar, plus the uniform
            // administrative-app page padding (24px) as the single "air" every
            // screen gets. This lives here (a border-box frame) rather than on
            // the scroll container below, so the padding never overflows it.
            paddingTop: `calc(${APP_BAR_HEIGHT + sectionTabsHeight}px + ${theme.spacing(3)})`,
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
            paddingBottom: theme.spacing(3),
            boxSizing: 'border-box',
            overflow: 'hidden',
          }),
        },

        '& .RaLayout-content > div': {
          width: '100%',
          backgroundColor: theme.palette.background.default,
          WebkitOverflowScrolling: 'touch',
          // This element is the single vertical scroll region AND hosts each
          // screen's sticky SectionHeader. A top padding here (the page
          // wrapper's `p:2`) pushes a `position:sticky; top:0` header's pin
          // point below the padding edge, opening a slit between the app bar
          // and the pinned header through which scrolled rows leak. Zero the
          // top padding so the header pins flush under the app bar; the side
          // and bottom insets are kept.
          paddingTop: 0,

          ...(!isMobile && {
            borderRadius: 0,
            boxShadow: 'none',
            overflow: 'auto',
            height: '100%',
          }),

          ...(isMobile && {
            flex: 1,
            minHeight: 0,
            borderRadius: 0,
            boxShadow: 'none',
            overflowY: 'auto',
            overflowX: 'hidden',
            maxWidth: '100vw',
          }),
        },
      }}
    >
      {content}
    </Layout>
  );
};

export default CustomLayout;
